import { useCallback, useEffect, useState } from 'react';

// BO 系列存档：localStorage 持久化 + 内存中提供 CRUD
//
// 存档对象形状（V1）：
// {
//   id: 'bo_<timestamp>',              // 唯一 ID
//   savedAt: <ms>,                     // 存档时间戳
//   schemaVersion: 1,
//   label: '2026-05-26 · BO5',         // 可重命名
//   blueName: '蓝方', redName: '红方', // 可重命名
//   mode: 'BO1' | 'BO3' | 'BO5',
//   totalGames: 5,
//   firstSide: 'blue',                 // 系列首局先手
//   fearlessEnabled: true,
//   scores: { blue: 3, red: 2 },       // 自动派生自 games[].winner
//   games: [
//     {
//       gameIndex, firstSide,
//       bluePicks: [{ id, idName, name }],   // 注意只存轻量字段，避免 localStorage 爆
//       redPicks:  [...], blueBans: [...], redBans: [...],
//       winner: 'blue' | 'red' | null,
//     }
//   ]
// }
//
// 注意：存档时不存 squareImg / loadingImg / splashImg（这些是 CDN URL，可以根据 idName 还原）

const STORAGE_KEY = 'lol_coach_bo_archive_v1';
const MAX_ARCHIVES = 200; // 软上限，超过时按 savedAt 升序 truncate（先删最老）

// ---- 工具函数 ----

// 把 champion 对象瘦身到必要字段
// - 保留 squareImg：让 SeriesReportCard 不依赖 useChampionData 就能渲染
// - loadingImg URL 是版本无关的，渲染时拼即可
const slimChamp = (c) => {
  if (!c || !c.id) return null;
  return {
    id: c.id,
    idName: c.idName || String(c.id),
    name: c.name || '',
    squareImg: c.squareImg || '',
  };
};

const slimList = (arr) => (arr || []).map(slimChamp);

const safeParse = (raw) => {
  try { return JSON.parse(raw); } catch { return null; }
};

const loadAllFromStorage = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const arr = safeParse(raw);
    return Array.isArray(arr) ? arr : [];
  } catch {
    return [];
  }
};

const saveAllToStorage = (list) => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(list));
  } catch (err) {
    // 5MB 写满或浏览器禁用 localStorage 时静默失败
    console.warn('[useBOArchive] save failed:', err);
  }
};

// ---- 时间标签 ----

const formatLabel = (mode, when = Date.now()) => {
  const d = new Date(when);
  const pad = (n) => String(n).padStart(2, '0');
  const ymd = `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
  const hm  = `${pad(d.getHours())}:${pad(d.getMinutes())}`;
  return `${ymd} ${hm} · ${mode || 'BO'}`;
};

// ---- 标准化函数：把 useCoachBO 当前状态 → 存档对象 ----
//
// 用法：
//   const archive = makeArchive({
//     mode: bo.mode, totalGames: bo.totalGames,
//     firstSide: bo.completedGames[0]?.firstSide || bo.currentFirstSide,
//     fearlessEnabled: bo.fearlessEnabled,
//     games: bo.completedGames, // 整个 completedGames 数组
//     blueName, redName, winners: [...], label,
//   });
export const makeArchive = ({
  mode,
  totalGames,
  firstSide,
  fearlessEnabled,
  games,
  blueName = '蓝方',
  redName = '红方',
  winners = [],   // 每局胜者，索引对齐 games[gameIndex]: 'blue' | 'red' | null
  label,
}) => {
  const now = Date.now();
  const id = `bo_${now}_${Math.random().toString(36).slice(2, 8)}`;
  const gamesPayload = (games || []).map((g) => ({
    gameIndex: g.gameIndex,
    firstSide: g.firstSide || 'blue',
    bluePicks: slimList(g.bluePicks),
    redPicks:  slimList(g.redPicks),
    blueBans:  slimList(g.blueBans),
    redBans:   slimList(g.redBans),
    winner: winners[g.gameIndex] || null,
  }));
  const scores = gamesPayload.reduce(
    (acc, g) => {
      if (g.winner === 'blue') acc.blue += 1;
      if (g.winner === 'red')  acc.red  += 1;
      return acc;
    },
    { blue: 0, red: 0 },
  );
  return {
    id,
    savedAt: now,
    schemaVersion: 1,
    label: label || formatLabel(mode, now),
    blueName, redName,
    mode,
    totalGames,
    firstSide: firstSide || 'blue',
    fearlessEnabled: Boolean(fearlessEnabled),
    scores,
    games: gamesPayload,
  };
};

// ============ Hook ============

const useBOArchive = () => {
  const [archives, setArchives] = useState(() => loadAllFromStorage());

  // 写入 localStorage（list 变化时）
  useEffect(() => {
    saveAllToStorage(archives);
  }, [archives]);

  // 创建：把新存档放到最前面（最新在顶部）
  const addArchive = useCallback((archive) => {
    if (!archive || !archive.id) return null;
    setArchives((prev) => {
      const next = [archive, ...prev.filter((a) => a.id !== archive.id)];
      // 软上限：按 savedAt 降序保留前 MAX_ARCHIVES
      return next.slice(0, MAX_ARCHIVES);
    });
    return archive.id;
  }, []);

  const removeArchive = useCallback((id) => {
    setArchives((prev) => prev.filter((a) => a.id !== id));
  }, []);

  const renameArchive = useCallback((id, patch) => {
    setArchives((prev) => prev.map((a) => (a.id === id ? { ...a, ...patch } : a)));
  }, []);

  // 更新某局的胜负标记（用于"补填胜负"功能）
  const setGameWinner = useCallback((archiveId, gameIndex, winner /* 'blue'|'red'|null */) => {
    setArchives((prev) => prev.map((a) => {
      if (a.id !== archiveId) return a;
      const games = a.games.map((g) =>
        (g.gameIndex === gameIndex ? { ...g, winner: winner || null } : g));
      const scores = games.reduce(
        (acc, g) => {
          if (g.winner === 'blue') acc.blue += 1;
          if (g.winner === 'red')  acc.red  += 1;
          return acc;
        },
        { blue: 0, red: 0 },
      );
      return { ...a, games, scores };
    }));
  }, []);

  const clearAll = useCallback(() => {
    setArchives([]);
  }, []);

  // 导出单个存档为 JSON 文件
  const downloadArchive = useCallback((archive) => {
    if (!archive) return;
    const blob = new Blob([JSON.stringify(archive, null, 2)], {
      type: 'application/json;charset=utf-8',
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    const safeLabel = (archive.label || archive.id).replace(/[\\/:*?"<>|]/g, '_');
    link.href = url;
    link.download = `${safeLabel}.json`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    setTimeout(() => URL.revokeObjectURL(url), 500);
  }, []);

  // 导出全部存档为单一 JSON 文件
  const downloadAll = useCallback(() => {
    const blob = new Blob(
      [JSON.stringify({ schemaVersion: 1, exportedAt: Date.now(), archives }, null, 2)],
      { type: 'application/json;charset=utf-8' },
    );
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `lol_bo_archive_${new Date().toISOString().slice(0, 10)}.json`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    setTimeout(() => URL.revokeObjectURL(url), 500);
  }, [archives]);

  // 从 JSON 文件导入（支持单 archive 或 {archives: [...]} 形状）
  const importFromFile = useCallback(async (file) => {
    if (!file) return { ok: false, count: 0, msg: 'no file' };
    try {
      const text = await file.text();
      const parsed = JSON.parse(text);
      let incoming = [];
      if (parsed && Array.isArray(parsed.archives)) incoming = parsed.archives;
      else if (parsed && parsed.id && Array.isArray(parsed.games)) incoming = [parsed];
      else return { ok: false, count: 0, msg: '无法识别的存档文件结构' };
      let added = 0;
      setArchives((prev) => {
        const map = new Map(prev.map((a) => [a.id, a]));
        incoming.forEach((a) => {
          if (a && a.id) {
            map.set(a.id, a);
            added += 1;
          }
        });
        const merged = Array.from(map.values())
          .sort((x, y) => (y.savedAt || 0) - (x.savedAt || 0))
          .slice(0, MAX_ARCHIVES);
        return merged;
      });
      return { ok: true, count: added };
    } catch (err) {
      return { ok: false, count: 0, msg: String(err && err.message || err) };
    }
  }, []);

  return {
    archives,
    addArchive,
    removeArchive,
    renameArchive,
    setGameWinner,
    clearAll,
    downloadArchive,
    downloadAll,
    importFromFile,
  };
};

export default useBOArchive;
